// @flow
import { InputAdornment, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import type {inputType} from '@shared/model/components';
import { cloneElement, useState } from 'react';
import { useI18n } from '@shared/helper/hooks';
import findNestedValue from '@shared/utils/findNestedValue';
import { NumericFormat } from 'react-number-format';
import HelperText from '@shared/components/helperText';
import { isNullOrUndefined } from '@shared/utils';

function Input({
	label,
	name,
	control,
	errors,
	schema,
	type = 'text',
	defaultValue = '',
	comment,
	variant = 'standard',
	startIcon,
	endIcon,
	isCurrencyInput = false,
	...textFieldProps
}: inputType): React$Node {
	const { translate } = useI18n('finoShared');
	const [isRequired] = useState(
		schema
			?.describe()
			?.fields[name]?.tests?.some((test) => test.name === 'required') ||
        false,
	);

	return (
		<Controller
			name={name}
			defaultValue={defaultValue}
			control={control}
			render={({ field }) => {
				const newField = { ...field };
				newField.value ||= '';

				function updateValue(event) {
					const value = event?.target?.value;
					const newValue = value.match(/-?\d+/g)?.toString().replaceAll(',', '');

					if (Number.isNaN(Number(newValue))) {
						newField.onChange('');
						return;
					}

					newField.onChange(Number(newValue));
				}

				const foundError = findNestedValue(errors, name);

				const currencyInputProps = {
					customInput: TextField,
					valueIsNumericString: true,
					thousandSeparator: true,
					thousandsGroupStyle: 'thousand',
					decimalSeparator: '.',
					displayType: 'input',
					// suffix: translate('rial'),
					allowNegative: false,
					value: newField?.value?.toLocaleString(),
					onChange: updateValue,
					onBlur: updateValue,
				};

				const MainInput = isCurrencyInput ? <NumericFormat
					{...currencyInputProps}
				/> : <TextField {...newField} />;

				return (
					<>
						{cloneElement(MainInput, {
							label,
							type,
							...(type === 'number' && { onWheel: event => event.target.blur() }),
							variant,
							fullWidth: true,
							required: isRequired,
							error: Boolean(foundError),
							helperText: <HelperText message={foundError?.message || comment} />,
							InputProps: {
								startAdornment: startIcon && (
									<InputAdornment position={'start'}>{startIcon}</InputAdornment>
								),
								endAdornment: (endIcon || isCurrencyInput) && (
									<InputAdornment position={'end'}>
										{isCurrencyInput && isNullOrUndefined(endIcon) && translate('rial')}
										{endIcon}
									</InputAdornment>
								),
							},
							...textFieldProps,
							sx: {
								...textFieldProps?.sx,
								'.MuiInputBase-input': {
									direction: 'rtl',
									textAlign: 'left',
									...textFieldProps?.sx?.['.MuiInputBase-input'],
								},
							}
						})
						}
					</>
				);
			}}
		/>
	);
}

export default Input;
